import React, { useMemo, useState } from "react"
import { Plus } from "lucide-react"

const StatusBadge = ({ label, statusClass }) => (
  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${statusClass}`}>
    {label}
  </span>
)

const AnnualGoalsAdminList = ({
  adminStaff = [],
  loading = false,
  statusByEmail = {},
  statusesLoading = false,
  currentYear,
  getStatusLabel,
  getStatusClass,
  onInitiateGoalPlanning,
  onInitiateMidYear,
  onInitiateEndYear,
  onOpenConference,
  onOpenCheckInConference,
  onViewGoalPlanning,
  onViewCheckIn,
}) => {
  const [searchQuery, setSearchQuery] = useState("")

  const filteredStaff = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return adminStaff

    return adminStaff.filter((admin) => {
      const fullName = `${admin.firstName || ""} ${admin.lastName || ""}`.toLowerCase()
      const email = (admin.email || "").toLowerCase()
      return fullName.includes(query) || email.includes(query)
    })
  }, [adminStaff, searchQuery])

  const getAdminStatus = (admin) => {
    const data = statusByEmail[admin.email?.toLowerCase()] || {
      goalPlannings: [],
      checkIns: [],
    }

    const goalPlanning = data.goalPlannings.find((gp) => gp.year === currentYear)
    const midYear = data.checkIns.find(
      (item) => item.checkInType === "mid_year" && item.year === currentYear
    )
    const endYear = data.checkIns.find(
      (item) => item.checkInType === "end_of_year" && item.year === currentYear
    )

    const hasActiveGoal =
      goalPlanning?.status === "initiated" ||
      goalPlanning?.status === "submitted_by_admin"
    const hasCompletedGoal = goalPlanning?.status === "completed"
    const canInitiateMidYear = hasCompletedGoal && !midYear
    const canInitiateEndYear = midYear?.status === "completed" && !endYear
    const hasActiveMidYear =
      midYear?.status === "initiated" || midYear?.status === "submitted_by_admin"
    const hasActiveEndYear =
      endYear?.status === "initiated" || endYear?.status === "submitted_by_admin"

    return {
      goalPlanning,
      midYear,
      endYear,
      hasActiveGoal,
      hasCompletedGoal,
      canInitiateMidYear,
      canInitiateEndYear,
      hasActiveMidYear,
      hasActiveEndYear,
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="font-semibold text-[#241763]">Admin Staff</h3>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full sm:w-72 p-2 border rounded text-sm"
          aria-label="Search admin staff"
        />
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-gray-100 font-bold">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Goal Planning</th>
              <th className="p-3 text-left">Mid-Year Check-in</th>
              <th className="p-3 text-left">End-of-Year Reflection</th>
            </tr>
          </thead>
          <tbody>
            {loading || statusesLoading ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  Loading admin staff and statuses...
                </td>
              </tr>
            ) : filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  No admin staff found.
                </td>
              </tr>
            ) : (
              filteredStaff.map((admin) => {
                const status = getAdminStatus(admin)

                return (
                  <tr key={admin.id} className="border-t align-top hover:bg-gray-50">
                    <td className="p-3 font-medium whitespace-nowrap">
                      {admin.firstName} {admin.lastName}
                    </td>
                    <td className="p-3 text-gray-600">{admin.email}</td>

                    <td className="p-3">
                      <div className="flex flex-col gap-2 items-start">
                        <StatusBadge
                          label={
                            status.goalPlanning
                              ? getStatusLabel(status.goalPlanning.status)
                              : "Not Started"
                          }
                          statusClass={
                            status.goalPlanning
                              ? getStatusClass(status.goalPlanning.status)
                              : "bg-gray-100 text-gray-600"
                          }
                        />
                        {!status.goalPlanning && (
                          <button
                            type="button"
                            onClick={() => onInitiateGoalPlanning(admin)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            <Plus className="h-3 w-3" />
                            Goal Planning
                          </button>
                        )}
                        {status.goalPlanning?.status === "submitted_by_admin" && (
                          <button
                            type="button"
                            onClick={() =>
                              onOpenConference(admin, status.goalPlanning)
                            }
                            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            Complete Conference
                          </button>
                        )}
                        {status.goalPlanning?.status === "completed" && (
                          <button
                            type="button"
                            onClick={() =>
                              onViewGoalPlanning(admin, status.goalPlanning)
                            }
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                          >
                            View
                          </button>
                        )}
                        {status.hasActiveGoal &&
                          status.goalPlanning?.status === "initiated" && (
                            <span className="text-xs text-gray-500">
                              Waiting for admin
                            </span>
                          )}
                      </div>
                    </td>

                    <td className="p-3">
                      <div className="flex flex-col gap-2 items-start">
                        <StatusBadge
                          label={
                            status.midYear
                              ? getStatusLabel(status.midYear.status)
                              : "Not Started"
                          }
                          statusClass={
                            status.midYear
                              ? getStatusClass(status.midYear.status)
                              : "bg-gray-100 text-gray-600"
                          }
                        />
                        {status.canInitiateMidYear && (
                          <button
                            type="button"
                            onClick={() => onInitiateMidYear(admin)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#5B9BD5] text-white rounded text-xs hover:opacity-90"
                          >
                            <Plus className="h-3 w-3" />
                            Mid-Year Check-in
                          </button>
                        )}
                        {status.midYear?.status === "submitted_by_admin" && (
                          <button
                            type="button"
                            onClick={() =>
                              onOpenCheckInConference(admin, status.midYear)
                            }
                            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            Complete Conference
                          </button>
                        )}
                        {status.midYear?.status === "completed" && (
                          <button
                            type="button"
                            onClick={() => onViewCheckIn(admin, status.midYear)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                          >
                            View
                          </button>
                        )}
                        {!status.hasCompletedGoal && !status.midYear && (
                          <span className="text-xs text-gray-500">
                            Complete Goal Planning first
                          </span>
                        )}
                        {status.hasActiveMidYear &&
                          status.midYear?.status === "initiated" && (
                            <span className="text-xs text-gray-500">
                              Waiting for admin
                            </span>
                          )}
                      </div>
                    </td>

                    <td className="p-3">
                      <div className="flex flex-col gap-2 items-start">
                        <StatusBadge
                          label={
                            status.endYear
                              ? getStatusLabel(status.endYear.status)
                              : "Not Started"
                          }
                          statusClass={
                            status.endYear
                              ? getStatusClass(status.endYear.status)
                              : "bg-gray-100 text-gray-600"
                          }
                        />
                        {status.canInitiateEndYear && (
                          <button
                            type="button"
                            onClick={() => onInitiateEndYear(admin)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#5B9BD5] text-white rounded text-xs hover:opacity-90"
                          >
                            <Plus className="h-3 w-3" />
                            End-of-Year Reflection
                          </button>
                        )}
                        {status.endYear?.status === "submitted_by_admin" && (
                          <button
                            type="button"
                            onClick={() =>
                              onOpenCheckInConference(admin, status.endYear)
                            }
                            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            Complete Conference
                          </button>
                        )}
                        {status.endYear?.status === "completed" && (
                          <button
                            type="button"
                            onClick={() => onViewCheckIn(admin, status.endYear)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                          >
                            View
                          </button>
                        )}
                        {!status.canInitiateEndYear &&
                          !status.endYear &&
                          status.hasCompletedGoal && (
                            <span className="text-xs text-gray-500">
                              Complete Mid-Year first
                            </span>
                          )}
                        {status.hasActiveEndYear &&
                          status.endYear?.status === "initiated" && (
                            <span className="text-xs text-gray-500">
                              Waiting for admin
                            </span>
                          )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AnnualGoalsAdminList
